import React, { FC, forwardRef, RefObject, useState } from 'react';
import styled, { css } from 'styled-components';
import { getButtonStyles } from '../../utils/getButtonStyles';

import { FormField } from './FormField';

interface SelectOptions {
  name: string;
  value: string;
}
interface SelectButtonProps {
  label: string;
  id: string;
  valid?: boolean;
  invalid?: boolean;
  required?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  options: SelectOptions[];
  iconLeft?: React.ReactNode;
}
interface StyledSelectButtonProps {
  disabled?: boolean;
  valid?: boolean;
  invalid?: boolean;
}

const OUTLINE_STYLES = 'inset 0 0 0 2px';

const StyledSelectButton = styled.select<StyledSelectButtonProps>`
  ${({ theme: { colors, maxMedia }, disabled, valid, invalid }) => css`
    ${getButtonStyles('pill', false, disabled)};
    appearance: none;
    padding-left: 40px;

    // Valid state

    ${valid &&
    css`
      box-shadow: ${OUTLINE_STYLES} ${colors.valid.default};

      &:focus,
      &:hover {
        box-shadow: ${OUTLINE_STYLES} ${colors.valid.default};
      }
    `}

    // Invalid state

    ${invalid &&
    css`
      box-shadow: ${OUTLINE_STYLES} ${colors.invalid.default};

      &:focus,
      &:hover {
        box-shadow: ${OUTLINE_STYLES} ${colors.invalid.default};
      }
    `}
  `};
`;

const StyledSelectButtonWrapper = styled.div`
  ${({ theme: { space, colors, maxMedia } }) => css`
    position: relative;

    svg {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      left: ${space.md};
    }

    &:hover {
      svg {
        color: ${colors.brand.default};
      }
    }
  `};
`;

export const SelectButton: FC<SelectButtonProps> = forwardRef(
  ({ disabled, label, id, valid, invalid, required, options, errorMessage, iconLeft, className, ...rest }, ref) => {
    const IconLeft = iconLeft;
    return (
      <StyledSelectButtonWrapper className={className}>
        <FormField
          label={label}
          htmlFor={id}
          required={required}
          invalid={invalid}
          valid={valid}
          errorMessage={errorMessage}
          hideLabel
        />
        {IconLeft || ''}
        <StyledSelectButton id={id} disabled={disabled} ref={ref as RefObject<HTMLSelectElement>} {...rest}>
          {options?.length >= 1 &&
            options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.name}
              </option>
            ))}
        </StyledSelectButton>
      </StyledSelectButtonWrapper>
    );
  }
);
